// The Code tab (`/p/<Key>/code`, and `/p/<Key>/code/<id>` for a session open
// in it): the project's sessions on the left; in the middle the
// open session, its status strip, its transcript, what it asks docked under
// it, and where he prompts it; on the right, when asked for, its issue and
// its subagents.
// Moved from today's Code tab (code/CodePage.tsx there), on the new
// server's store: the sessions and their questions are the board's, the
// runners are read as the page opens, and the open session's transcript is
// followed while it is open (sessions/follow.ts). How each session's side
// pane stands is remembered in this browser, its width with it.
import { LiveChecks } from '../checks/progress'
import { lazy, Suspense, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { errorMessage } from '../api/client'
import type { Issue, Me, Question, Runner, Session } from '../api/types'
import { uuid4 } from '../api/uuid'
import { NotFound } from '../components/layout'
import { toast } from '../components/toasts'
import { Working } from '../components/Working'
import { sessionPath } from '../projects/routing'
import { answeredOf, askedAt, useQuestionEntries, type AskRecord } from '../questions/asks'
import { ExpiredQuestion } from '../questions/ExpiredQuestion'
import { expiredOf } from '../questions/expired'
import { useExpiryClock } from '../questions/waiting'
import { useRunners, useTerminalClient, useTranscript } from '../sessions/context'
import { useBoardState, useStore } from '../store/context'
import { needsServer } from '../components/needsServer'
import { Transcript, type ExpiredQuestions } from '../transcript/Transcript'
import { lastReply } from './Usage'
import { cx } from '../transcript/bits'
import type { TranscriptView } from '../sessions/follow'
import { engineAgentsOf } from './EngineAgents'
import type { UserItem } from '../transcript/types'
import { actorName } from '../vocab'
import type { RunRequest } from './terminal/types'
import { ChangesPane } from './ChangesPane'
import { Composer } from './Composer'
import { isBusy, leadOf, openQuestions, runnerClosed, titleOf, workersOf } from './format'
import { IssuePane } from './IssuePane'
import { ListIcon, PlusIcon, SidePaneIcon } from './icons'
import { NewSessionDialog } from './NewSession'
import { QuestionDock } from './QuestionDock'
import { nativeTerminal } from './TerminalHandoff'
import { clampPane, PANE_TABS, RightPane, useViewportWidth, type PaneTab } from './RightPane'
import { SessionList } from './SessionList'
import { StatusStrip } from './StatusStrip'
import { SubAgentsPane } from './SubAgentsPane'
import { useKnownTitles, useNoteTitle } from './titles'
import { useProjectTitle } from '../components/useDocumentTitle'
import { useWorkspaceDraft } from '../workspace/drafts'

const TerminalPane = lazy(() => import('./terminal/TerminalPane').then(module => ({default: module.TerminalPane})))

/** Each session's own open state, tab and width, in this browser. */
const PANE_KEY = 'poulpe.code.pane'

interface PaneState {
  open: boolean
  width: number
  tab: PaneTab
}

const PANE_DEFAULT: PaneState = { open: false, width: 460, tab: 'issue' }

const isWidth = (width: unknown): width is number => typeof width === 'number' && Number.isFinite(width)

export function readPane(session: string): PaneState {
  try {
    const held = JSON.parse(window.localStorage.getItem(`${PANE_KEY}.${session}`) ?? 'null') as Partial<PaneState> | null
    return {
      open: held?.open === true,
      width: isWidth(held?.width) ? held.width : PANE_DEFAULT.width,
      tab: PANE_TABS.some((entry) => entry.tab === held?.tab) ? (held!.tab as PaneTab) : PANE_DEFAULT.tab,
    }
  } catch {
    return PANE_DEFAULT
  }
}

function savePane(session: string, pane: PaneState) {
  try {
    window.localStorage.setItem(`${PANE_KEY}.${session}`, JSON.stringify({ open: pane.open, tab: pane.tab, width: pane.width }))
  } catch {
    // Without storage the pane opens as it does the first time.
  }
}

const toastError = (action: string, err: unknown) => toast(`${action}: ${errorMessage(err)}`, 'error')

/** Ending a session is for good: the server keeps no way to resume it. */
const END_QUESTION = 'End the session? It takes no more prompts once ended.'

export function CodePage() {
  useProjectTitle('Code')
  const { id: sessionId } = useParams()
  const { sessions, issues, questions, pendingAnswers, projects, selectedProjectId, me, offline } = useBoardState()
  const store = useStore()
  const navigate = useNavigate()
  const location = useLocation()
  const listId = useId()
  const { runners } = useRunners()
  const titles = useKnownTitles()
  const asked = useMemo(() => openQuestions(questions, pendingAnswers), [questions, pendingAnswers])

  const selected = sessionId ?? null
  const session = selected === null ? undefined : sessions.get(selected)
  const transcript = useTranscript(session ?? null)
  // The project the list shows: the open session's, or the address's (ProjectRoute selected it).
  const project = session?.project_id ?? selectedProjectId
  // A session opened under another project's address moves to its own project's.
  const own = session && session.project_id !== selectedProjectId ? projects.get(session.project_id) : undefined
  useLayoutEffect(() => {
    if (own && session) navigate(sessionPath(own.key, session.id), { replace: true, state: location.state })
  }, [own, session, navigate, location.state])

  const listed = useMemo(() => [...sessions.values()].filter((s) => s.project_id === project), [sessions, project])
  const titleFor = useCallback((of: Session) => titleOf(of, of.issue_id ? issues.get(of.issue_id) : undefined, titles.get(of.id)), [issues, titles])

  const terminal = useTerminalClient(session?.id ?? null)
  const viewport = useViewportWidth()
  const [paneStates, setPaneStates] = useState<ReadonlyMap<string, PaneState>>(() => new Map())
  const pane = selected === null ? (paneStates.get('') ?? PANE_DEFAULT) : (paneStates.get(selected) ?? readPane(selected))
  const setPaneOf = useCallback((of: string, change: Partial<PaneState>) => {
    setPaneStates((held) => {
      const next = { ...(held.get(of) ?? (of === '' ? PANE_DEFAULT : readPane(of))), ...change }
      if (of !== '') savePane(of, next)
      return new Map(held).set(of, next)
    })
  }, [])
  const setPane = (change: Partial<PaneState>) => setPaneOf(selected ?? '', change)
  // The agent's own CLI drives the session: its terminal comes to the front (Poulpe-555).
  const native = nativeTerminal(session)
  useEffect(() => {
    if (native !== null && selected !== null) setPaneOf(selected, { open: true, tab: 'terminal' })
  }, [native, selected, setPaneOf])
  const [listOpen, setListOpen] = useState(false)
  // A click on Run in a reply: bound, at the click, to this session's terminal client and the
  // folder the runner resolved for it. The pane is told that folder as its session's, so it owns
  // the request when it first draws it; it types the command and presses Enter, as today's does.
  const [run, setRun] = useState<RunRequest | null>(null)
  const onRun = useCallback(
    (command: string) => {
      if (!terminal || selected === null) return
      const id = selected
      terminal.list().then(
        (answer) => {
          setRun({ client: terminal, session: id, cwd: answer.cwd, intent: {}, command })
          setPaneOf(id, { open: true, tab: 'terminal' })
        },
        (error: unknown) => toast(`The command was not run: ${errorMessage(error)}`, 'error'),
      )
    },
    [terminal, selected, setPaneOf],
  )
  const [dialog, setDialog] = useWorkspaceDraft('session.new.open', false, (value): value is boolean => typeof value === 'boolean', () => false)
  // The sessions he ended whose stop the server has not answered yet. Once it has, the session's
  // own `stopping` says it is ending, until its runner ends it or refuses the stop.
  const [sending, setSending] = useState<ReadonlySet<string>>(() => new Set())
  const ending = useMemo(() => {
    const all = new Set([...sessions.values()].filter((s) => s.stopping && s.state !== 'ended').map((s) => s.id))
    for (const id of sending) if (sessions.get(id)?.state !== 'ended') all.add(id)
    return all
  }, [sessions, sending])
  // The sessions he ended, each with whether the server has accepted the stop. The store holds its answer
  // by then, or a newer session: one accepted that is neither stopping nor ended was refused by its runner,
  // even when the feed brought the acceptance and the refusal together and the page never drew it stopping.
  const [ended] = useState(() => new Map<string, boolean>())
  useEffect(() => {
    for (const [id, accepted] of ended) {
      const of = sessions.get(id)
      if (!of || of.state === 'ended') ended.delete(id)
      else if (accepted && !of.stopping) {
        ended.delete(id)
        toast('The runner did not end the session.', 'error')
      }
    }
  }, [sessions, sending, ended])

  // Minimise on a worker's own page comes here, to its lead's, and names the worker: the lead's side pane
  // opens on SubAgents with that worker shown. Once for each arrival: what he does with the pane after is his.
  const [arrivals] = useState(() => new Set<string>())
  const [minimised, setMinimised] = useState<{ lead: string; subagent: string } | null>(null)
  useEffect(() => {
    if (arrivals.has(location.key)) return
    arrivals.add(location.key)
    const subagent = (location.state as { subagent?: unknown } | null)?.subagent
    if (typeof subagent !== 'string' || selected === null) {
      setMinimised(null)
      return
    }
    setPaneOf(selected, { open: true, tab: 'subagents' })
    setMinimised({ lead: selected, subagent })
  }, [arrivals, location.key, location.state, selected, setPaneOf])
  const onSubagentOpened = useCallback(() => setMinimised(null), [])

  const end = (of: Session) => {
    if (store.getState().offline) return
    if (!window.confirm(END_QUESTION)) return
    setSending((held) => new Set(held).add(of.id))
    ended.set(of.id, false)
    const answered = () => setSending((held) => new Set([...held].filter((id) => id !== of.id)))
    store.sendSessionOp({ mutation_id: uuid4(), op: 'stop_session', args: { session_id: of.id } }).then(() => {
      if (ended.has(of.id)) ended.set(of.id, true)
      answered()
    }, (err: unknown) => {
      answered()
      ended.delete(of.id)
      toastError('Could not end the session', err)
    })
  }

  const onStarted = (started: Session) => {
    setDialog(false)
    const key = projects.get(started.project_id)?.key
    if (key) navigate(sessionPath(key, started.id))
  }

  const paneWidth = clampPane(pane.width, viewport)
  const togglePane = () => setPane({ open: !pane.open })
  const issue = session?.issue_id ? issues.get(session.issue_id) : undefined
  const runner = session ? runners?.find((r) => r.id === session.runner_id) : undefined
  const isEnding = session !== undefined && ending.has(session.id)

  return (
    <div className={cx('code-page', pane.open && 'has-pane', listOpen && 'is-list-open')} style={{ '--code-pane': `${paneWidth}px` } as CSSProperties}>
      <SessionList
        id={listId}
        sessions={listed}
        issues={issues}
        asked={asked}
        runners={runners}
        titles={titles}
        selected={selected}
        ending={ending}
        onNew={() => {
          setListOpen(false)
          setDialog(true)
        }}
        onClose={() => setListOpen(false)}
      />
      {listOpen && <div className="code-backdrop" aria-hidden="true" onClick={() => setListOpen(false)} />}

      <section className={cx('code-main', isEnding && 'is-ending')} aria-label={session ? titleFor(session) || 'Session' : 'Code'}>
        {selected === null ? (
          <>
            <header className="code-strip">
              <div className="code-strip-top">
                <button type="button" className="button button-quiet code-icon-button code-list-button" aria-label="Sessions" aria-controls={listId} title="Sessions" onClick={() => setListOpen(true)}>
                  <ListIcon size={15} />
                </button>
                <h1 className="code-title">Code</h1>
                <span className="code-strip-actions">
                  <button
                    type="button"
                    className={cx('button button-quiet code-icon-button', pane.open && 'is-active')}
                    aria-label="Side pane"
                    aria-pressed={pane.open}
                    title={pane.open ? 'Hide the side pane' : 'Show the side pane: Changes, Issue, SubAgents'}
                    onClick={togglePane}
                  >
                    <SidePaneIcon size={15} />
                  </button>
                </span>
              </div>
            </header>
            <div className="code-state code-empty">
              <p>
                <strong>No session is open.</strong>
              </p>
              <p className="muted">Pick one from the list, or start one.</p>
              <button type="button" className="button button-primary" disabled={offline} {...needsServer(offline)} onClick={() => setDialog(true)}>
                <PlusIcon size={13} />
                New session
              </button>
            </div>
          </>
        ) : !session ? (
          <NotFound what="session" />
        ) : (
          <SessionView
            view={transcript}
            key={session.id}
            session={session}
            title={titleFor(session) || (leadOf(session) ? 'Untitled subagent' : 'Untitled session')}
            issue={issue}
            asked={asked.get(session.id) ?? []}
            runner={runner}
            runners={runners}
            me={me}
            paneOpen={pane.open}
            onTogglePane={togglePane}
            onShowList={() => setListOpen(true)}
            onRun={terminal ? onRun : undefined}
            onEnd={session.state !== 'ended' && runnerClosed(runner) === null ? () => end(session) : undefined}
            ending={isEnding}
            leadTitle={(() => {
              const lead = leadOf(session)
              const of = lead ? sessions.get(lead) : undefined
              return of ? titleFor(of) || undefined : undefined
            })()}
          />
        )}
      </section>

      {pane.open && (
        <RightPane
          tab={pane.tab === 'terminal' && !terminal ? 'issue' : pane.tab}
          onTab={(tab) => setPane({ tab })}
          width={paneWidth}
          viewport={viewport}
          onWidth={(width) => setPane({ width })}
          onClose={() => setPane({ open: false })}
          subagents={workersOf(sessions.values(), session?.id).length + engineAgentsOf(transcript.subagents ?? []).length}
          terminal={terminal && session ? <Suspense fallback={<Working label="Loading the terminal" />}><TerminalPane client={terminal} runRequest={run} front={native} canClose={!offline} session={{id: session.id, cwd: run?.session === session.id ? run.cwd : null, worktree: null}} unavailable={offline ? 'The board is offline. Input was stopped.' : session.state === 'ended' ? 'This session has ended.' : null} /></Suspense> : undefined}
          changes={<ChangesPane session={session ?? null} />}
          issue={<IssuePane issueId={session?.issue_id ?? null} issue={issue} />}
          subagentsPane={
            <SubAgentsPane
              session={session ?? null}
              transcript={transcript}
              sessions={sessions}
              runners={runners}
              titleOf={titleFor}
              asked={asked}
              me={me}
              ending={ending}
              onEnd={end}
              openSubagent={minimised !== null && minimised.lead === session?.id ? minimised.subagent : null}
              onSubagentOpened={onSubagentOpened}
            />
          }
        />
      )}

      {dialog && <NewSessionDialog projectId={project ?? null} onStarted={onStarted} onClose={() => setDialog(false)} />}
    </div>
  )
}

/** The open session: its strip, its transcript, what it asks, and where he prompts it. */
function SessionView({
  view,
  session,
  title,
  issue,
  asked,
  runner,
  runners,
  me,
  paneOpen,
  onTogglePane,
  onShowList,
  onRun,
  onEnd,
  ending,
  leadTitle,
}: {
  view: TranscriptView
  session: Session
  title: string
  issue: Issue | undefined
  asked: readonly Question[]
  runner: Runner | undefined
  runners: readonly Runner[] | null
  me: Me | null
  paneOpen: boolean
  onTogglePane: () => void
  onShowList: () => void
  /** Run a reply's shell block in the session's terminal; none without a terminal. */
  onRun?: (command: string) => void
  onEnd?: () => void
  ending: boolean
  leadTitle?: string
}) {
  useNoteTitle(session.id, view.info)
  const asking = asked.length > 0
  const askRecord = useAskRecord(session)
  const { expired, live } = useExpired(session, asked)
  return (
    <>
      <StatusStrip
        session={session}
        title={title}
        issue={issue}
        asking={asking}
        runners={runners}
        me={me}
        paneOpen={paneOpen}
        onTogglePane={onTogglePane}
        onShowList={onShowList}
        onEnd={onEnd}
        ending={ending}
        leadTitle={leadTitle}
        // Only once the server has brought it up to date: an earlier or a saved reply would date the cache wrongly.
        replied={view.caughtUp ? lastReply(view.items) : null}
      />
      {/* While it ends, what it says and takes is set back and takes nothing. The strip above
          stays as it is, with the list and the pane: he goes on elsewhere meanwhile. */}
      <div className="code-session-body" inert={ending} aria-busy={ending || undefined}>
        <LiveChecks session={session.id} />
        <Transcript view={view} me={me} busy={isBusy(session)} askRecord={askRecord} onRun={onRun} expired={expired} />
        {/* What the session asks waits after what it said, above where it is prompted; once expired, in the transcript. */}
        <QuestionDock session={session.id} questions={live} expired={expired.questions} />
        <Composer session={session} runner={runner} asking={asking} commands={view.info.commands} />
      </div>
      {ending && (
        <p className="code-ending" role="status">
          <span className="code-ending-label">
            <Working label="Ending the session…" aria-hidden="true" />
            Ending the session…
          </span>
        </p>
      )}
    </>
  )
}

/**
 * The session's expired questions, which its transcript shows where they
 * were asked (questions/expired.ts), and the others, which the dock shows;
 * one moves from the dock as its expiry passes.
 */
function useExpired(session: Session, asked: readonly Question[]): { expired: ExpiredQuestions; live: readonly Question[] } {
  const { questions } = useBoardState()
  const byQuestion = useQuestionEntries()
  const now = useExpiryClock(asked)
  const asker = actorName({ kind: 'agent', agent: session.agent, session: session.id })
  // The list it was, while it holds the same questions: the transcript places them again only when they change.
  const held = useRef<Question[]>([])
  const found = expiredOf(questions.values(), session.id, now)
  if (found.length !== held.current.length || found.some((question, i) => question !== held.current[i])) held.current = found
  const shown = held.current
  const expired = useMemo<ExpiredQuestions>(
    () => ({
      questions: shown,
      askedAt: (id) => askedAt(id, byQuestion),
      render: (question) => <ExpiredQuestion question={question} asker={asker} />,
    }),
    [shown, byQuestion, asker],
  )
  const ids = new Set(shown.map((question) => question.id))
  return { expired, live: asked.filter((question) => !ids.has(question.id)) }
}

/**
 * What the transcript shows in place of a prompt that carried an answer to
 * one of the session's questions: the question and its answer, as the board
 * keeps them; null, and the prompt shows as sent, when it does not say.
 */
function useAskRecord(session: Session): (item: UserItem) => AskRecord | null {
  const { questions } = useBoardState()
  const byQuestion = useQuestionEntries()
  const asker = actorName({ kind: 'agent', agent: session.agent, session: session.id })
  return useCallback(
    (item: UserItem) => {
      if (!item.question) return null
      const answered = answeredOf(item.question, item, questions, byQuestion)
      return answered && { ...answered, asker }
    },
    [questions, byQuestion, asker],
  )
}
